import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { InnerPageRequirements } from "@/components/site/InnerPageRequirements";
import { RentalRequirements } from "@/components/site/RentalRequirements";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { breadcrumbScript } from "@/lib/schema";

const bookingFaqs = [
  {
    q: "What's the minimum age to rent?",
    a: "All drivers need to be at least 25 and carry a full, unrestricted license. Novice (N) and Learner (L) classes don't qualify.",
  },
  {
    q: "What should I have on me at pickup?",
    a: "Bring two pieces of ID (one must be a valid driver's license) along with a major credit card (Visa, Mastercard, or American Express) in the renter's name to secure the deposit hold.",
  },
  {
    q: "Are hourly rentals available?",
    a: "We rent by the day only. Each day starts the moment you pick up and lasts a full 24 hours.",
  },
  {
    q: "Is delivery an option?",
    a: "Yes. Delivery is free within Vancouver and to YVR Airport. Other Metro Vancouver locations, the Fraser Valley, and Whistler are available by arrangement; contact us to confirm delivery pricing.",
  },
  {
    q: "What payment methods do you accept?",
    a: "Plenty of ways. The rental balance can be paid by all major credit cards (Visa, Mastercard, American Express), Interac e-Transfer for Canadian customers, PayPal, cryptocurrency such as Bitcoin or Ethereum (ask us about other coins), or cash. The refundable deposit hold still needs to go on a credit card in the renter's name.",
  },
  {
    q: "Do you cater to weddings, music videos, or film shoots?",
    a: "Frequently. They're some of the bookings we enjoy most. Get in touch for a tailored quote, and we'll guide you through dressing the car up safely.",
  },
];

const drivingFaqs = [
  {
    q: "How much mileage is included each day?",
    a: "Short-term rentals come with 150 km per day. Weekly and monthly bookings carry their own allowances, all spelled out in the rental agreement. Overage rates appear at checkout.",
  },
  {
    q: "Is the car cleaned and fuelled before I get it?",
    a: "Every time. Vehicles are professionally detailed and topped off before delivery. Just return it at the same fuel level, or add our refuelling service for a small charge.",
  },
  {
    q: "Will the car look like a rental?",
    a: "Not at all. No stickers, no rental plates, no logos. Just the car itself.",
  },
  {
    q: "Can I take it off-road or smoke inside?",
    a: "No. The cars are strictly for paved roads and are 100% smoke and vape free. GPS alerts catch violations the moment they happen, which terminates the rental and brings cleaning and loss-of-use charges.",
  },
  {
    q: "Do the vehicles have GPS tracking?",
    a: "They do, but we only check the data if a car comes back late or rental terms are broken (heavy speeding, off-road driving, and the like). Everyday driving remains your business.",
  },
];

const coverageFaqs = [
  {
    q: "What kind of insurance comes with the rental?",
    a: "Each rental includes $5,000,000 of third-party liability coverage. Loss Damage Waiver (LDW) is $60 per day on every vehicle; its deductible and terms are shown in the rental agreement.",
  },
  {
    q: "How is the security deposit handled?",
    a: "The refundable security deposit is $2,000 for every vehicle. At delivery we pre-authorize it on a major credit card in the renter's name, then release the hold after the car is returned and passes inspection.",
  },
  {
    q: "What happens if the car gets damaged?",
    a: "Every rental includes LDW. Your financial responsibility is limited by the deductible and terms in your signed rental agreement; prohibited use can void the waiver.",
  },
];

const sections = [
  { title: "Booking", heading: "Booking made easy.", items: bookingFaqs },
  { title: "Driving & Pickup", heading: "Out on the road.", items: drivingFaqs },
  { title: "Insurance & Deposit", heading: "Coverage, explained.", items: coverageFaqs },
];

const allFaqs = sections.flatMap((s) => s.items);

export const Route = createFileRoute("/faq")({
  component: FAQ,
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "FAQ | Avolus Cars Vancouver" },
      {
        name: "description",
        content:
          "Answers on age, license, deposit, delivery, insurance, mileage, and payment for renting a luxury or exotic car from Avolus in Vancouver.",
      },
      { property: "og:title", content: "FAQ | Avolus Luxury Car Rental" },
      { property: "og:description", content: "Everything you need to know before booking a luxury or exotic rental with Avolus." },
      { property: "og:url", content: "https://avoluscars.com/faq" },
    ],
    links: [{ rel: "canonical", href: "https://avoluscars.com/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: allFaqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      breadcrumbScript([
        { name: "Home", url: "https://avoluscars.com/" },
        { name: "FAQ", url: "https://avoluscars.com/faq" },
      ]),
    ],
  }),
});

function FaqGroup({ title, heading, items, startIndex = 0 }: { title: string; heading: string; items: { q: string; a: string }[]; startIndex?: number }) {
  return (
    <div className="mb-16 last:mb-0">
      <Reveal>
        <Eyebrow>{title}</Eyebrow>
        <h2 className="font-display text-3xl md:text-4xl text-foreground mb-10">
          {heading}
        </h2>
      </Reveal>
      <Reveal>
        <Accordion type="single" collapsible className="w-full">
          {items.map((f, i) => (
            <AccordionItem key={i} value={`item-${startIndex + i}`} className="border-border">
              <AccordionTrigger className="text-left text-base md:text-lg font-display text-foreground py-6 hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  );
}

function FAQ() {
  let cursor = 0;
  return (
    <Layout inner>
      <section className="pt-40 pb-12">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Frequently Asked Questions</Eyebrow>
            <h1 className="font-display text-5xl md:text-6xl text-foreground">
              Frequently Asked Questions
            </h1>
            <p className="mt-6 text-muted-foreground max-w-xl">
              Just the essentials. If something isn't here, get in touch and we'll answer you directly.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="hidden md:block"><RentalRequirements showFaqLink={false} /></div>
      <div className="md:hidden"><InnerPageRequirements /></div>

      <section className="py-24 lg:py-32 border-t border-border">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          {sections.map((s) => {
            const start = cursor;
            cursor += s.items.length;
            return (
              <FaqGroup key={s.title} title={s.title} heading={s.heading} items={s.items} startIndex={start} />
            );
          })}
        </div>
      </section>
    </Layout>
  );
}
